/**
 * AstralTeleportSystem — Astral-tree teleport event hub.
 *
 * Phase Step is the mechanical axis of the entire Astral tree. At least
 * eight other nodes react to teleport events: spatial_memory, warp_geometry,
 * dense, rift_path_passive, oracle_phase_step, temporal_echo_upgrade_2, and
 * prescience. Centralising teleport event broadcasting here prevents each
 * node from needing a direct reference to PhaseStepSkillEffect.
 *
 * Responsibilities:
 * - Execute a teleport: start the invulnerability window (if one is set),
 *   then broadcast a TeleportEvent to all registered listeners.
 * - Track the invulnerability window timer (ticked via update()).
 * - Allow oracle_phase_step to override the invuln duration during
 *   Oracle / Prescience mode via setInvulnOverride().
 *
 * Blink itself has no invulnerability window (override = null → 0ms).
 * Phase Step sets an override of 250ms on unlock. oracle_phase_step can
 * temporarily raise the override to 500ms.
 *
 * Pure logic — no Phaser dependencies.
 *
 * @see astral-spec.md § AstralTeleportSystem
 * @see ADR-064 (attunement shared systems architecture)
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface TeleportEvent {
  fromX: number;
  fromY: number;
  toX: number;
  toY: number;
  /** Wall-clock timestamp in ms (Date.now()). */
  timestamp: number;
}

// ---------------------------------------------------------------------------
// System
// ---------------------------------------------------------------------------

export class AstralTeleportSystem {
  /**
   * Registered teleport listeners. Snapshotted on fire to guard against
   * listeners that synchronously register new listeners.
   */
  private readonly _listeners: Array<(event: TeleportEvent) => void> = [];

  /**
   * Remaining invulnerability window in ms. Counts down via update().
   * Zero means not invulnerable.
   */
  private _invulnTimer = 0;

  /**
   * Overridden invulnerability duration in ms, or null if none is set.
   * When null, executeTeleport starts no invuln window.
   * Set by PhaseStepSkillEffect on unlock (250ms) and by
   * oracle_phase_step during Oracle/Prescience (500ms).
   */
  private _invulnOverride: number | null = null;

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------

  /**
   * Execute a teleport: start the invuln window (if an override is set),
   * then broadcast the event to all registered listeners.
   */
  executeTeleport(fromX: number, fromY: number, toX: number, toY: number): void {
    const invulnMs = this.getInvulnDurationMs();
    if (invulnMs > 0) {
      this._invulnTimer = invulnMs;
    }

    const event: TeleportEvent = {
      fromX,
      fromY,
      toX,
      toY,
      timestamp: Date.now(),
    };

    // Snapshot to avoid mutation issues if a listener modifies the registry.
    const snapshot = this._listeners.slice();
    for (const cb of snapshot) {
      cb(event);
    }
  }

  /**
   * Register a listener for teleport events.
   * Called by any node that reacts to Phase Step / Blink.
   */
  onTeleport(callback: (event: TeleportEvent) => void): void {
    this._listeners.push(callback);
  }

  /**
   * Whether the invulnerability window from the last teleport is still active.
   */
  isInvulnerable(): boolean {
    return this._invulnTimer > 0;
  }

  /**
   * Current invulnerability window duration in ms.
   * Returns 0 if no override is set (Blink mode).
   */
  getInvulnDurationMs(): number {
    return this._invulnOverride ?? 0;
  }

  /**
   * Set or clear the invulnerability window duration.
   * - Pass 250 when Phase Step unlocks.
   * - Pass 500 when oracle_phase_step activates during Oracle/Prescience.
   * - Pass null to clear (reverts Blink's no-invuln behaviour).
   */
  setInvulnOverride(ms: number | null): void {
    this._invulnOverride = ms;
  }

  // ---------------------------------------------------------------------------
  // Lifecycle
  // ---------------------------------------------------------------------------

  /** Per-frame: tick the invulnerability timer down. */
  update(delta: number): void {
    if (this._invulnTimer > 0) {
      this._invulnTimer = Math.max(0, this._invulnTimer - delta);
    }
  }

  /** Release all state and listeners. */
  destroy(): void {
    this._listeners.length = 0;
    this._invulnTimer = 0;
    this._invulnOverride = null;
  }
}
